import { useTranslation } from 'react-i18next'

import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import { SPOT_TYPES, V2_SPOT_TYPE_LABELS } from '../utils/constants/db.constants'
import { DEFAULT_SPOT_DEADBAND, DEFAULT_SPOT_RADIUS } from '../utils/constants/geoxp.constants'
import AgamiTypeSelector from '../ViewUpload/AgamiTypeSelector'
import SpotPositionSelector from '../ViewUpload/SpotPositionSelector'
import SpotPositionPreview from '../components/SpotPositionPreview'
import SpotManualCodeSelector from '../ViewUpload/SpotManualCodeSelector'
import LandingPageSelector from '../ViewUpload/LandingPageSelector'
import { AgamiUploadFormData } from '../ViewUpload/UploadAgamiInterface'

interface AgamiActivationBlocksProps {
  formData: AgamiUploadFormData
  setFormData: (data: AgamiUploadFormData | ((prev: AgamiUploadFormData) => AgamiUploadFormData)) => void
  handleDataChange: <K extends keyof AgamiUploadFormData>(key: K, newValue: AgamiUploadFormData[K]) => void
  checkInputIsValid: <K extends keyof AgamiUploadFormData>(
    key: K,
    condition: boolean,
    fallbackVal?: AgamiUploadFormData[K]
  ) => void
  uploading: boolean
  isPublic: boolean
  /** Present when editing: the manual-code uniqueness check excludes the spot itself. */
  spotId?: string
}

/**
 * The activation blocks of the v2 form (06.2): the RECOMPOSED part — five of
 * the v1's eight extracted components, none rewritten.
 *
 * The type selector reads V2_SPOT_TYPE_LABELS (an application constant), so
 * `custom` exists here and only here: getSpotTypes(), the v1's source, filters
 * on `is_active = true` and that filter is what keeps custom out of the form
 * every venue uses today. Its visibility is gated by the same subscription
 * feature as the v1 (manual_spots), and `custom` needs no block of its own:
 * nothing about its activation is configured by the author (06.3).
 *
 * Radius and deadband are NOT defaulted into the data: like the v1, the columns
 * stay NULL unless the author sets them, and the listener apps fall back to the
 * experience defaults. The constants below only feed the map preview.
 */
const AgamiActivationBlocks: React.FC<AgamiActivationBlocksProps> = ({
  formData,
  setFormData,
  handleDataChange,
  checkInputIsValid,
  uploading,
  isPublic,
  spotId,
}) => {
  const { t } = useTranslation()
  const manualSpotsAuth = useFeatureAuth(FEATURES.MANUAL_SPOTS)

  return (
    <>
      {manualSpotsAuth && (
        <AgamiTypeSelector
          isPublic={isPublic}
          formData={formData}
          handleDataChange={handleDataChange}
          availableSpotTypes={V2_SPOT_TYPE_LABELS}
          // The custom label says what WILL happen ("the word activates inside
          // the app"), not what is missing — "no activation" would be false: a
          // trigger exists, it just is not GeoXp's business (06.3).
          displayLabels={{ [SPOT_TYPES.CUSTOM]: t('uploadV2.type.custom') }}
        />
      )}

      {/* GEO TYPE */}
      {formData.type === SPOT_TYPES.GEO && (
        <div className="flex flex-col gap-4 bg-white dark:bg-primary rounded-lg p-4 shadow-sm">
          <SpotPositionSelector
            formData={formData}
            setFormData={setFormData}
            handleDataChange={handleDataChange}
            checkInputIsValid={checkInputIsValid}
            uploading={uploading}
          />
          <SpotPositionPreview
            interactive
            latitude={formData.latitude}
            setLatitude={(lat) => handleDataChange('latitude', lat)}
            longitude={formData.longitude}
            setLongitude={(lon) => handleDataChange('longitude', lon)}
            radius={formData.radius ?? DEFAULT_SPOT_RADIUS}
            deadband={formData.deadband ?? DEFAULT_SPOT_DEADBAND}
          />
        </div>
      )}

      {/* MANUAL TYPE */}
      {formData.type === SPOT_TYPES.MANUAL && (
        <div className="flex flex-col gap-4 bg-white dark:bg-primary rounded-lg p-4 shadow-sm">
          <SpotManualCodeSelector
            formData={formData}
            uploading={uploading}
            handleDataChange={handleDataChange}
            spotId={spotId}
          />
          <LandingPageSelector
            value={formData.landing_page_code_id}
            onChange={(id) => handleDataChange('landing_page_code_id', id)}
            disabled={uploading}
          />
        </div>
      )}
    </>
  )
}

export default AgamiActivationBlocks
